/**
 * RosterSlot.jsx
 * 
 * Phase 2F UI Remediation - Extracted Component
 * Phase 1A: Live Outcomes - Resolution Display
 * 
 * Purpose: Interactive roster position card for GM Mode roster building.
 * Displays empty state with position label, or filled state with player selection.
 * Supports locked state for post-deadline rosters.
 * Shows resolution status (WIN/LOSS/PUSH) when available.
 * 
 * @component
 * @example
 * // Resolved slot with outcome
 * <RosterSlot 
 *   slotId={1} 
 *   label="QB"
 *   selection={{ 
 *     player: { name: 'P. Mahomes', team: 'KC' },
 *     prop: { stat: 'Pass Yards', line: 260.5 },
 *     side: 'OVER',
 *     units: 20 
 *   }}
 *   resolution={{
 *     status: 'win',
 *     payout: 18.18,
 *     actualValue: 287,
 *     resolvedAt: '2024-01-15T12:00:00Z'
 *   }}
 *   isLocked={true}
 * />
 */

import React from 'react';
import { Plus, Lock, Check, X, Minus, Clock } from 'lucide-react';

// Position color palette - factual indicators by position group
const POSITION_COLORS = {
  QB: '#6366F1',   // Indigo
  RB: '#0EA5E9',   // Sky blue
  WR: '#8B5CF6',   // Purple
  TE: '#06B6D4',   // Cyan
  FLEX: '#F59E0B', // Amber
};

// Resolution status colors - Phase 1A
// HIT = prediction correct, MISS = prediction incorrect (game plan terminology, not betting)
const RESOLUTION_COLORS = {
  hit: { bg: '#10B981', text: '#FFFFFF', icon: Check },    // Green
  miss: { bg: '#EF4444', text: '#FFFFFF', icon: X },       // Red
  push: { bg: '#6B7280', text: '#FFFFFF', icon: Minus },   // Gray
  void: { bg: '#6B7280', text: '#FFFFFF', icon: Minus },   // Gray
  pending: { bg: '#F59E0B', text: '#FFFFFF', icon: Clock }, // Amber
  // Legacy support during transition
  win: { bg: '#10B981', text: '#FFFFFF', icon: Check },
  loss: { bg: '#EF4444', text: '#FFFFFF', icon: X },
};

/**
 * RosterSlot - Interactive roster position card
 * 
 * @param {Object} props
 * @param {number} props.slotId - Unique slot identifier
 * @param {string} props.label - Position label (QB, RB1, WR2, TE, FLEX)
 * @param {Object} [props.selection] - Current selection data
 * @param {Object} [props.selection.player] - Selected player object
 * @param {string} [props.selection.player.name] - Player name
 * @param {string} [props.selection.player.team] - Player team abbreviation
 * @param {Object} [props.selection.prop] - Selected prop
 * @param {string} [props.selection.prop.stat] - Prop stat type
 * @param {number} [props.selection.prop.line] - Prop line value
 * @param {string} [props.selection.side] - OVER or UNDER
 * @param {number} [props.selection.units] - Units allocated
 * @param {boolean} [props.isLocked=false] - Whether roster is locked (post-deadline)
 * @param {function} props.onClick - Click handler for slot interaction
 */
const RosterSlot = ({ 
  slotId, 
  label, 
  selection, 
  resolution = null,
  isLocked = false, 
  onClick 
}) => {
  const isEmpty = !selection?.player;
  
  // Get position color from label (strip numbers: RB1 -> RB)
  const positionKey = label.replace(/[0-9]/g, '');
  const posColor = POSITION_COLORS[positionKey] || '#64748B'; // slate-500 fallback
  
  // Slot click handler - respects locked state
  const handleClick = () => {
    if (isLocked) return;
    onClick?.();
  };
  
  return (
    <div 
      className={`
        relative rounded-xl p-2.5 transition-all
        ${isLocked 
          ? 'cursor-default opacity-80' 
          : 'cursor-pointer active:scale-[0.98]'
        }
        ${isEmpty 
          ? 'border-2 border-dashed border-slate-300 bg-slate-50' 
          : 'border-2 bg-white shadow-sm'
        }
      `}
      style={{ 
        borderColor: isEmpty ? undefined : `${posColor}60`  // 60 = ~38% opacity
      }} 
      onClick={handleClick}
      role="button"
      tabIndex={isLocked ? -1 : 0}
      aria-label={isEmpty 
        ? `Add player to ${label} slot` 
        : `${selection.player.name} - ${selection.prop?.stat || 'No prop'} - $${selection.units}`
      }
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Left accent bar for filled slots */}
      {!isEmpty && (
        <div 
          className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl" 
          style={{ backgroundColor: posColor }} 
        />
      )}
      
      {/* Lock indicator */}
      {isLocked && !isEmpty && (
        <div className="absolute top-1 right-1">
          <Lock size={12} className="text-slate-400" />
        </div>
      )}
      
      <div className={isEmpty ? '' : 'pl-2'}>
        {/* Header row: position badge + resolution status + units */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span 
              className={`
                text-[10px] font-bold px-1.5 py-0.5 rounded-full
                ${isEmpty 
                  ? 'bg-slate-200 text-slate-500' 
                  : 'text-white'
                }
              `}
              style={{ backgroundColor: isEmpty ? undefined : posColor }}
            >
              {label}
            </span>
            
            {/* Resolution status badge */}
            {!isEmpty && resolution && (
              (() => {
                const status = (resolution.status || 'pending').toLowerCase();
                const config = RESOLUTION_COLORS[status] || RESOLUTION_COLORS.pending;
                const IconComponent = config.icon;
                return (
                  <span 
                    className="flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase"
                    style={{ backgroundColor: config.bg, color: config.text }}
                  >
                    <IconComponent size={10} strokeWidth={3} />
                    {status}
                  </span>
                );
              })()
            )}
          </div>
          
          {!isEmpty && (
            <span className="text-sm font-black text-indigo-600 tabular-nums">
              {`$${selection.units}`}
            </span>
          )}
        </div>
        
        {/* Content: Empty state or player info */}
        {isEmpty ? (
          <div className="mt-1.5 flex items-center gap-1 text-slate-400">
            <Plus size={14} strokeWidth={2.5} />
            <span className="text-xs font-medium">Tap to select</span>
          </div>
        ) : (
          <div className="mt-1">
            <p className="font-bold text-slate-900 text-sm truncate">
              {selection.player.name}
            </p>
            <p className="text-[11px] text-slate-500">
              <span className="font-semibold text-slate-700">
                {selection.side}
              </span>{' '}
              {selection.prop?.stat}
              {selection.prop?.line && (
                <span className="ml-1 tabular-nums">
                  {selection.prop.line}
                  {/* Show actual value if resolved */}
                  {resolution?.actualValue != null && (
                    <span className="text-slate-400"> → </span>
                  )}
                  {resolution?.actualValue != null && (
                    <span className={['hit', 'win'].includes(resolution.status?.toLowerCase()) ? 'text-emerald-600 font-semibold' : ['miss', 'loss'].includes(resolution.status?.toLowerCase()) ? 'text-red-500 font-semibold' : ''}>
                      {resolution.actualValue}
                    </span>
                  )}
                </span>
              )}
            </p>
            {/* Resolution payout row */}
            {resolution && resolution.status !== 'pending' && resolution.payout != null && (
              <p className={`text-xs font-bold tabular-nums mt-0.5 ${resolution.payout >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {resolution.payout >= 0 ? '+' : ''}{resolution.payout.toFixed(2)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Export position colors for use in other components
export { POSITION_COLORS };
export default RosterSlot;
